import { useState, useMemo } from 'react'
import { Button, Paper, tss, Divider, Box } from '@ds/components'
import { MagazinePage } from '@types/magazine'
import Article from './Article'

/**
 * This component is used to display a "Show More" button that loads more items when clicked.
 * We had to recreate this component because the original one was not working with static content.
 */

const useStyles = tss
    .withName('ArticleListWithShowMore')
    .withNestedSelectors<'dense' | 'isGrid' | 'showButtonDivider'>()
    .create(({ theme, classes }) => ({
        paper: {
            marginBottom: theme.spacing(2),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),

            [theme.breakpoints.up('sm')]: {
                paddingTop: theme.spacing(3),
                paddingBottom: theme.spacing(3),

                [`&.${classes.dense}`]: {
                    paddingTop: theme.spacing(2),
                    paddingBottom: theme.spacing(2),
                },
            },
        },
        dense: {},
        isGrid: {},
        showButtonDivider: {},
        spacingTop: {
            marginTop: theme.spacing(2),

            [theme.breakpoints.up('sm')]: {
                marginTop: theme.spacing(3),

                [`&.${classes.dense}`]: {
                    marginTop: theme.spacing(2),
                },
            },
        },
        spacingBottom: {
            marginBottom: theme.spacing(2),

            [theme.breakpoints.up('sm')]: {
                marginBottom: theme.spacing(3),

                [`&.${classes.dense}`]: {
                    marginBottom: theme.spacing(2),
                },
            },
        },
        marginBottom: {
            marginBottom: theme.spacing(2),
        },
        buttonWrapper: {
            textAlign: 'center',
            marginBottom: 0,
            paddingTop: theme.spacing(2),

            [`&.${classes.showButtonDivider}`]: {
                paddingTop: 0,
            },

            [theme.breakpoints.up('sm')]: {
                marginBottom: theme.spacing(-1),

                [`&.${classes.dense}:not(.${classes.isGrid})`]: {
                    marginBottom: 0,
                },
            },
        },
    }))

const ArticleListWithShowMore = ({
    initialArticleDisplay = 3,
    showMoreAmount = 5,
    initialData,
}: {
    initialData: MagazinePage[]
    initialArticleDisplay?: number
    showMoreAmount?: number
}) => {
    const { classes, cx } = useStyles()
    const [visibleItemCount, setVisibleItemCount] = useState(initialArticleDisplay)

    const handleLoadMore = () => {
        setVisibleItemCount((prevCount: number) => prevCount + showMoreAmount)
    }

    const divider = useMemo(
        () => <Divider className={cx(classes.spacingTop, classes.spacingBottom, classes.dense)} />,
        [classes.dense, classes.spacingBottom, classes.spacingTop, cx],
    )

    const displayedData = initialData.slice(0, visibleItemCount)

    return (
        <Paper className={cx(classes.paper, classes.dense)}>
            {displayedData.map((item, index) => (
                <Article
                    key={index}
                    article={item}
                    divider={displayedData && index !== displayedData.length - 1 ? divider : undefined}
                />
            ))}
            {initialData.length > displayedData.length ? (
                <>
                    {divider}
                    <Box className={cx(classes.buttonWrapper, classes.dense, classes.showButtonDivider)}>
                        <Button onClick={handleLoadMore} fullWidth={true} variant="text">
                            Show more
                        </Button>
                    </Box>
                </>
            ) : null}
        </Paper>
    )
}

export default ArticleListWithShowMore
